<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { seo } from '~/data/pages/banks/mocks'
import type { IResponseData } from '~/types'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import type { IMicroCredit } from '~/components/entity/microcredit/item'
import { WidgetOrganizationAsideContacts } from '~/components/widget/organization-aside-contacts'
import { WidgetBankServicesMenu } from '~/components/widget/bank'
import { navItems } from '~/data/pages/microcredit/mocks'
import { WidgetAboutPageHeader } from '~/components/widget/about-page'
import { WidgetCreditsList } from '~/components/widget/credits'
import { useSeoFromCustom } from '~/composables/useSeoFromCustom'
import { WidgetSeoContent } from '~/components/widget/seo'

const { t, locale } = useI18n()
useSeoMeta(seo)
const route = useRoute()
const url = `/microcredit/${route.params.microcredit}`
const { data } = await useApiWith404<IResponseData<IMicroCredit>>(() => url, {
  key: `microcredit-page-${route.params.slug}`
})
const microcredit = computed(() => data.value?.data)
useEmptySectionNoindex(microcredit, 'credits_count')

const requestUrl = useRequestURL()
const baseUrl = requestUrl.origin

const seoData = computed(() => ({
  organization: {
    title: microcredit.value?.title
  }
}))

useSeoFromCustom(seoData, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.microcredits'), url: '/microcredit' },
  {
    title: microcredit.value?.title || t('common.microcredits'),
    url: `/microcredit/${route.params.microcredit}`
  },
  { title: t('common.credits'), url: `/microcredit/${route.params.microcredit}/credits` }
])

const fitOrganization = computed(() => {
  return {
    type: microcredit.value?.type || '',
    title: microcredit.value?.title || '',
    logo: microcredit.value?.logo,
    organization: {
      official_title: microcredit.value?.official_title || ''
    },
    views_count: microcredit.value?.views_count || 0,
    updated_at: microcredit.value?.updated_at || ''
  }
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts
              v-if="microcredit"
              :organization="microcredit"
              :type="'microcredit'"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
          </aside>
        </template>
        <div class="space-y-5">
          <WidgetAboutPageHeader
            :type="'microcredit'"
            :product="fitOrganization"
            :heading="t('common.credits')"
          >
            <WidgetBankServicesMenu
              v-if="microcredit && microcredit.status === 'active'"
              :organization="microcredit"
              :nav-items="navItems"
              :type="'microcredit'"
            />
          </WidgetAboutPageHeader>
          <WidgetSeoContent
            heading-level="h3"
            leading-level="8"
            position="upper"
            :variables="{
              organization: microcredit?.official_title ?? ''
            }"
          />
          <WidgetCreditsList
            v-if="microcredit && microcredit.id"
            :hide-organization-details="true"
            :type="'microcredit'"
            :query-params="{
              per_page: '20',
              'filter[organization_id]': microcredit.id.toString(),
              'filter[organization_type]': microcredit.organization_type
            }"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
